import dynamic from 'next/dynamic'
import isEmpty from 'lodash/isEmpty'
import { useRef, useState, useEffect } from 'react'
import {
  useTheme,
  ChartContainer,
  ChoroplethRegion,
  CustomLinkChartProps,
  Datum,
  ReportKeyChartProps
} from '~/design_system'
import { SadStates } from '~/components/SadStates'
import usStateGeoData from './geodata/us_states.json'
import countriesGeoData from './geodata/world_countries.json'
import { CustomChoroplethLegend } from './CustomChoroplethLegend'
import { DefaultChartSkeleton } from './DefaultChartSkeleton'
import { useChartFormat, useFormattedTitle } from './format'
import { GenericChartProps } from '~/design_system/components/charts/ChartTypes'

// nivo elements have to be dynamically imported because d3 went ESM only and nextjs doesn't like it
const ResponsiveChoropleth = dynamic(() => import('@nivo/geo').then(m => m.ResponsiveChoropleth), { ssr: false })
const ThemeProvider = dynamic(() => import('@nivo/core').then(m => m.ThemeProvider), { ssr: false })

export const CHOROPLETH_REGION: { world: ChoroplethRegion; USA: ChoroplethRegion } = {
  world: { features: countriesGeoData.features, rotation: [0, 0, 0], scaleFactor: 1 },
  USA: { features: usStateGeoData, rotation: [97, -36, 1], scaleFactor: 600 }
}

/** Calculate domain (range of all possible values) for the given data */
const getDomain = (data: Datum[]): number[] => {
  const values = data.map(obj => (typeof obj.value === 'string' ? parseInt(obj.value) : obj.value))
  return [Math.min(...values), Math.max(...values)]
}

interface BaseRegionDataChoroplethProps extends GenericChartProps {
  region: ChoroplethRegion
  projectionScale?: number
}

interface CustomLinkRegionDataChoroplethProps extends BaseRegionDataChoroplethProps, CustomLinkChartProps {}

interface ReportKeyRegionDataChoroplethProps extends BaseRegionDataChoroplethProps, ReportKeyChartProps {}

export type RegionDataChoroplethProps = CustomLinkRegionDataChoroplethProps | ReportKeyRegionDataChoroplethProps

export const RegionDataChoropleth = ({
  region = CHOROPLETH_REGION.world,
  data,
  reportKey,
  customReportUrl,
  chartConfig,
  projectionScale: defaultProjectionScale,
  isLoading = true,
  isFetching = true,
  isError = false,
  errorCode
}: RegionDataChoroplethProps) => {
  const { title, subTitle, format } = chartConfig
  const { colors } = useTheme()
  const purples: string[] = Object.values(colors.purple)
  const valueFormat = useChartFormat('number')

  const containerRef = useRef<HTMLDivElement>(null)
  const DEFAULT_SCALE = 100
  const [projectionScale, setProjectionScale] = useState(region.scaleFactor * DEFAULT_SCALE)
  useEffect(() => {
    // Choropleth width/scale = parent width rounded down to nearest 50
    // e.g. parent width = 473, choropleth width will be = 450
    const scale = Math.min(
      Math.floor((containerRef.current?.offsetWidth || region.scaleFactor) / 50) * 50,
      region.scaleFactor * DEFAULT_SCALE
    )
    setProjectionScale(scale)
  }, [containerRef])

  return (
    <div ref={containerRef}>
      <ChartContainer
        title={useFormattedTitle(title, format)}
        subTitle={subTitle}
        reportKey={reportKey}
        customReportUrl={customReportUrl}
        isLoading={isLoading}
        isFetching={isFetching}
        isEmpty={data === undefined || isEmpty(data)}
        isError={errorCode === 403 && data === undefined ? false : isError}>
        <SadStates states={[{ when: isLoading, render: () => <DefaultChartSkeleton /> }]}>
          {data && (
            <>
              <ResponsiveChoropleth
                data={data}
                domain={getDomain(data)}
                features={region.features}
                projectionScale={defaultProjectionScale || projectionScale}
                projectionRotation={region.rotation}
                projectionTranslation={[0.5, 0.5]}
                colors={purples.slice(1, 8)}
                unknownColor="#fff"
                borderWidth={0.5}
                borderColor="grey"
                label={obj => obj.properties.name}
                valueFormat={valueFormat}
              />
              <ThemeProvider theme={{ legends: { text: { fontSize: 16 } } }}>
                <CustomChoroplethLegend data={data} colors={purples} valueFormat={valueFormat} />
              </ThemeProvider>
            </>
          )}
        </SadStates>
      </ChartContainer>
    </div>
  )
}
